import { type AbsenceApprovalDto, APPROVAL_DECISIONS, type CalendarEntryDto, type ConflictDto, LEAVE_KINDS } from '@bqcrm/shared';
import { Alert, Badge, Button, Card, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconCircleCheck, IconCircleDashed, IconCircleX, IconPlayerSkipForward } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { dayRange, formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { ConflictList, usePeople } from './common';

const VIA: Record<NonNullable<AbsenceApprovalDto['current']>['via'], string> = {
  drabinka: '',
  zastepstwo: ' (zastępstwo)',
  uprawnienie: ' (osoby zatwierdzające nieobecności)',
};

/** Przebieg akceptacji wniosku: poziomy drabinki z decyzjami i kto decyduje teraz (docs/17). */
export function ApprovalTrail({ approval }: { approval: AbsenceApprovalDto }) {
  return (
    <Stack gap={4}>
      {approval.steps.map((s) => {
        const Icon = s.decision === 'zatwierdzona' ? IconCircleCheck : s.decision === 'odrzucona' ? IconCircleX : s.decision === 'pominieta' ? IconPlayerSkipForward : IconCircleDashed;
        const color = s.decision === 'zatwierdzona' ? 'var(--mantine-color-green-6)' : s.decision === 'odrzucona' ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-dimmed)';
        return (
          <Group key={s.position} gap={6} wrap="nowrap" align="flex-start">
            <Icon size={16} color={color} style={{ marginTop: 2, flexShrink: 0 }} />
            <Text size="sm">
              {s.position}. {s.approverName}
              {s.decision && (
                <Text span c="dimmed" size="sm">
                  {' '}
                  — {APPROVAL_DECISIONS[s.decision].toLowerCase()}
                  {s.decidedByName && s.decidedByName !== s.approverName ? ` (w zastępstwie: ${s.decidedByName})` : ''}
                  {s.decidedAt ? `, ${formatDateTime(s.decidedAt)}` : ''}
                  {s.note ? ` · ${s.note}` : ''}
                </Text>
              )}
            </Text>
          </Group>
        );
      })}
      {approval.current && (
        <Text size="sm" c="dimmed">
          Czeka na decyzję: <b>{approval.current.names.join(', ') || '—'}</b>
          {VIA[approval.current.via]}
        </Text>
      )}
    </Stack>
  );
}

/** Zatwierdzenie (akceptacja poziomu) albo odmowa z obowiązkowym powodem — pracownik widzi go w aplikacji mobilnej. */
export function DecisionButtons({ entry, onDone }: { entry: CalendarEntryDto; onDone?: (r: { entry: CalendarEntryDto; conflicts: ConflictDto[] }) => void }) {
  const qc = useQueryClient();
  const [reject, setReject] = useState(false);
  const [note, setNote] = useState('');
  const steps = entry.approval?.steps.length ?? 0;
  const pos = entry.approval?.current?.position ?? null;
  const last = !steps || pos === null || pos === steps;
  const decide = useMutation({
    mutationFn: (v: { decision: 'zatwierdzona' | 'odrzucona'; note: string }) => api<{ entry: CalendarEntryDto; conflicts: ConflictDto[] }>(`/calendar/entries/${entry.id}/decision`, { method: 'POST', body: v }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['absences-pending'] });
      void qc.invalidateQueries({ queryKey: ['calendar'] });
      void qc.invalidateQueries({ queryKey: ['planning-board'] });
      notifyOk(r.entry.absenceStatus === 'zatwierdzona' ? 'Zatwierdzono nieobecność' : r.entry.absenceStatus === 'odrzucona' ? 'Odrzucono wniosek' : 'Zaakceptowano — wniosek przechodzi do kolejnej osoby');
      setReject(false);
      setNote('');
      onDone?.(r);
    },
    onError: notifyError,
  });
  return (
    <>
      <Group gap={4} wrap="nowrap">
        <Button size="xs" color="green" variant="light" loading={decide.isPending} onClick={() => decide.mutate({ decision: 'zatwierdzona', note: '' })}>
          {last ? 'Zatwierdź' : 'Zaakceptuj'}
        </Button>
        <Button size="xs" color="red" variant="subtle" disabled={decide.isPending} onClick={() => setReject(true)}>
          Odrzuć
        </Button>
      </Group>
      <Modal opened={reject} onClose={() => setReject(false)} title="Odrzucenie wniosku o nieobecność">
        <Stack gap="sm">
          <Text size="sm">
            {entry.title}, {dayRange(entry.dateFrom, entry.dateTo)}
          </Text>
          <Textarea label="Powód odmowy" description="Pracownik zobaczy go w aplikacji mobilnej" autosize minRows={3} maxLength={500} value={note} onChange={(e) => setNote(e.currentTarget.value)} data-autofocus />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setReject(false)}>
              Anuluj
            </Button>
            <Button color="red" disabled={note.trim().length < 3} loading={decide.isPending} onClick={() => decide.mutate({ decision: 'odrzucona', note })}>
              Odrzuć wniosek
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

/** Wnioski urlopowe czekające na moją decyzję — z przebiegiem akceptacji i listą kolidujących zadań po zatwierdzeniu. */
export function PendingAbsences() {
  const all = usePeople();
  const people = new Map((all.data ?? []).map((p) => [p.id, p.fullName]));
  const q = useQuery({ queryKey: ['absences-pending'], queryFn: () => api<CalendarEntryDto[]>('/calendar/absences/pending') });
  const [after, setAfter] = useState<ConflictDto[] | null>(null);
  if (!q.data?.length && !after) return null;
  return (
    <Card withBorder mb="md" padding="sm">
      <Text fw={600} size="sm" mb={4}>
        Wnioski o nieobecność do rozpatrzenia
      </Text>
      <Stack gap="sm">
        {q.data?.map((e) => (
          <Group key={e.id} justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap={2} style={{ flex: 1, minWidth: 240 }}>
              <Text size="sm">
                <b>{people.get(e.userIds[0] ?? '') ?? '—'}</b> — {e.title}
                {e.leaveKind && e.leaveKind !== 'wypoczynkowy' && !e.title.toLowerCase().includes(LEAVE_KINDS[e.leaveKind].toLowerCase()) ? ` (${LEAVE_KINDS[e.leaveKind].toLowerCase()})` : ''}, {dayRange(e.dateFrom, e.dateTo)}
                {e.timeFrom ? `, ${e.timeFrom}–${e.timeTo}` : ''}
                {e.note ? ` · ${e.note}` : ''}
              </Text>
              {!!e.approval?.steps.length && (
                <Group gap={6}>
                  <Badge size="sm" variant="light" color="yellow">
                    Poziom {e.approval.current?.position ?? '—'} z {e.approval.steps.length}
                  </Badge>
                  {e.approval.current?.via === 'zastepstwo' && (
                    <Badge size="sm" variant="light" color="grape">
                      W zastępstwie
                    </Badge>
                  )}
                  <Text size="xs" c="dimmed">
                    {e.approval.steps
                      .filter((s) => s.decision === 'zatwierdzona')
                      .map((s) => `zaakceptował(a): ${s.decidedByName ?? s.approverName}`)
                      .join(' · ')}
                  </Text>
                </Group>
              )}
            </Stack>
            <DecisionButtons entry={e} onDone={(r) => r.entry.absenceStatus === 'zatwierdzona' && r.conflicts.length && setAfter(r.conflicts)} />
          </Group>
        ))}
      </Stack>
      {after && (
        <Alert color="orange" mt="sm" title="Zatwierdzona nieobecność koliduje z zadaniami" withCloseButton onClose={() => setAfter(null)}>
          <ConflictList conflicts={after} />
        </Alert>
      )}
    </Card>
  );
}
